// ============================================================================
// ReturnsPanel — the shared returns surface mounted as a tab inside
// OMS → Operations (next to First Mile / Pending GRN / Pending Tasks).
// Same data + authorize action as the standalone /oms/returns route; the
// standalone route stays available for direct linking.
//
// The open worklist (everything not yet authorized) is fetched once and the stage
// tabs cut it in the browser, so every tab carries a live count and switching is
// instant. Only the history tabs (Authorized / All) go back to the server.
//
// Reports the open-return count up to the parent so Operations can render a badge.
// ============================================================================
import {
  ExclamationCircleOutlined, ExportOutlined, ReloadOutlined, UploadOutlined, WarningFilled,
} from "@ant-design/icons";
import {
  Button, Collapse, DatePicker, Descriptions, Dropdown, Input, Modal, Pagination, Select, Space, Switch,
  Table, Tag, Tooltip, Typography, message,
} from "antd";
import type { MenuProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import { useIsAdmin } from "../../../custom_hooks/use_is_admin";
import CopyText from "../components/copy_text";
import { CrmIssueDrawer } from "../components/crm_issue_thread";
import { fmtIST, omsOrderPath } from "../format";
import { useRememberedTab } from "../remembered_tab";
import { LogisticsOrderRow } from "../types";
import {
  TeamMember, getLogisticsRow, listTeamMembers, runLogisticsAction, updateLogisticsOrder,
} from "../logistics/logistics.api";
import { CARRIER_META, customerCell, dash, floorLift, locationCell, poCell } from "../logistics/board_columns";
import BookingDrawer from "../logistics/components/booking_drawer";
import EditLogisticsModal from "../logistics/components/edit_logistics_modal";
import { CITY_OPTIONS, UNASSIGNED_OWNER } from "../logistics/board_controls";
import IssueDrawer, { IssueTarget } from "../logistics/components/issue_drawer";
import ReassignModal from "../logistics/components/reassign_modal";
import RefundModal, { RefundModalContext } from "./components/refund_modal";
import ReschedulePickupModal from "./components/reschedule_pickup_modal";
import ChangeWarehouseModal from "./components/change_warehouse_modal";
import VendorReturnModal from "./components/vendor_return_modal";
import ReturnDelhiveryModal from "./components/return_delhivery_modal";
import {
  ReturnRow,
  ReturnRowAction,
  ReturnRowStatus,
  ReturnStageItem,
  VendorReturnStatus,
  VendorReturnSummary,
  cancelReturnBeforePickup,
  cancelVendorReturn,
  listReturns,
  markQcComplete,
  markVendorReturnDone,
} from "./returns.api";

// Where each return is, and what the desk does next with it.
const STAGE_META: Record<ReturnRowStatus, { label: string; color: string; next: string }> = {
  pending_booking: { label: "Awaiting booking", color: "default", next: "Book a carrier" },
  booked: { label: "Booked", color: "blue", next: "Waiting for pickup" },
  picked_up: { label: "In transit", color: "geekblue", next: "On the way to the warehouse" },
  received: { label: "Received", color: "gold", next: "Do QC" },
  qc_done: { label: "QC done", color: "orange", next: "Authorize the refund" },
  authorized: { label: "Refund authorized", color: "green", next: "Done" },
};

const VENDOR_RETURN_META: Record<VendorReturnStatus, { label: string; color: string }> = {
  awaiting_stock: { label: "Vendor return · waiting for stock-in", color: "default" },
  awaiting_bill: { label: "Vendor return · waiting for vendor bill", color: "gold" },
  filing_credit: { label: "Vendor return · filing credit", color: "blue" },
  credit_failed: { label: "Vendor return · credit failed", color: "red" },
  credit_filed: { label: "Vendor return · credit filed", color: "geekblue" },
  completed: { label: "Returned to vendor", color: "green" },
  cancelled: { label: "Vendor return cancelled", color: "default" },
};

const vendorReturnLive = (vr?: VendorReturnSummary | null) =>
  !!vr && vr.status !== "cancelled";

type StageTab =
  | "all_pending"
  | "pickup"
  | "qc_pending"
  | "authorize_pending"
  | "escalated"
  | "authorized"
  | "vendor_return"
  | "all";

const TAB_KEYS: StageTab[] = [
  "all_pending", "pickup", "qc_pending", "authorize_pending",
  "escalated", "authorized", "vendor_return", "all",
];
const HISTORY_TABS: StageTab[] = ["authorized", "vendor_return", "all"];

const TAB_LABEL: Record<StageTab, string> = {
  all_pending: "All open",
  pickup: "Pickup & transit",
  qc_pending: "QC pending",
  authorize_pending: "Authorize pending",
  escalated: "Escalated",
  authorized: "Authorized",
  vendor_return: "Vendor returns",
  all: "All",
};

// The open-worklist tabs, cut in the browser from the one "not yet authorized" fetch. Mirrors the
// server's filters for the same keys (oms/api/views/returns.py) over that set.
const IN_TAB: Partial<Record<StageTab, (r: ReturnRow) => boolean>> = {
  all_pending: () => true,
  pickup: (r) => r.status === "pending_booking" || r.status === "booked" || r.status === "picked_up",
  qc_pending: (r) => r.status === "received",
  authorize_pending: (r) => r.status === "qc_done",
  escalated: (r) => !!r.escalation,
};

const EMPTY_TEXT: Record<StageTab, string> = {
  all_pending: "No open returns 🎉",
  pickup: "Nothing waiting for pickup or on the way",
  qc_pending: "Nothing waiting for QC",
  authorize_pending: "No refunds waiting to be authorized",
  escalated: "No open escalations",
  authorized: "No authorized returns yet",
  vendor_return: "No vendor returns yet",
  all: "No returns yet",
};

const TAB_STORAGE_KEY = "oms.operations.returns.tab";

interface Props {
  // Parent (Operations tabs) renders a Badge with the open-return count.
  onCountChange?: (count: number) => void;
}

const FILTER_LABEL: React.CSSProperties = { fontSize: 12, color: "#8c8c8c", marginBottom: 2 };
const MUTED: React.CSSProperties = { fontSize: 12, color: "#8c8c8c" };
const ELLIPSIS: React.CSSProperties = {
  display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};

const isPrePickup = (r: ReturnRow) => r.status === "pending_booking" || r.status === "booked";

// The Last Mile board's own split (`attention._on_truck`): on a truck once the driver has collected,
// or while the booking is live with a vehicle stamped; otherwise it still needs a vehicle / trip.
const onTruck = (r: ReturnRow) =>
  r.status === "picked_up" || (r.logistics?.lifecycle === "active" && !!r.logistics?.vehicle_number?.trim());

const isOutstation = (r: ReturnRow) => r.delivery_city === "outstation";

// Return to vendor is not live yet: every RTV button stays hidden until this flips.
const RTV_ENABLED = false;

const PICKUP_SECTIONS: { key: string; label: string; help: string; match: (r: ReturnRow) => boolean }[] = [
  {
    key: "escalation",
    label: "Escalations — needs a decision",
    help: "Somebody raised an escalation on these returns, so they are out of the normal queue until it's resolved.",
    match: (r) => !!r.escalation,
  },
  {
    key: "to_assign",
    label: "Return — to assign",
    help: "Waiting to be collected from the customer, but no vehicle / trip yet.",
    match: (r) => !r.escalation && !isOutstation(r) && !onTruck(r),
  },
  {
    key: "on_truck",
    label: "Return — on a truck",
    help: "Vehicle assigned, or already collected and on the way to the warehouse.",
    match: (r) => !r.escalation && !isOutstation(r) && onTruck(r),
  },
  {
    key: "outstation",
    label: "Return — outstation",
    help: "Customer is outside every hub's zone — collected by an intercity / part-load partner.",
    match: (r) => !r.escalation && isOutstation(r),
  },
];

const QC_SECTIONS: typeof PICKUP_SECTIONS = [
  PICKUP_SECTIONS[0],
  {
    key: "qc",
    label: "Return — QC pending",
    help: "Received at the warehouse, waiting for QC.",
    match: (r) => !r.escalation,
  },
];

const TAB_SECTIONS: Partial<Record<string, typeof PICKUP_SECTIONS>> = {
  pickup: PICKUP_SECTIONS,
  qc_pending: QC_SECTIONS,
};

type LeadSpan = { pos: number; size: number };

const leadKey = (r: ReturnRow) => r.lead_id || `so:${r.so_id}`;

const pickupValue = (r: ReturnRow) => (r.pickup_date ? dayjs(r.pickup_date).valueOf() : Infinity);

const orderByLead = (rows: ReturnRow[]): { rows: ReturnRow[]; spans: Map<number, LeadSpan> } => {
  const groups = new Map<string, ReturnRow[]>();
  for (const r of rows) (groups.get(leadKey(r)) ?? groups.set(leadKey(r), []).get(leadKey(r))!).push(r);
  const ordered = Array.from(groups.values())
    .map((g) => [...g].sort((a, b) => pickupValue(a) - pickupValue(b)))
    .sort((a, b) => pickupValue(a[0]) - pickupValue(b[0]));
  const spans = new Map<number, LeadSpan>();
  for (const g of ordered) g.forEach((r, pos) => spans.set(r.stage_id, { pos, size: g.length }));
  return { rows: ordered.flat(), spans };
};

// Still waiting for the driver after the day the pickup was promised for.
const pickupOverdue = (r: ReturnRow) =>
  isPrePickup(r) && !!r.pickup_date && dayjs(r.pickup_date).isBefore(dayjs(), "day");

const matchesSearch = (row: ReturnRow, q: string) =>
  [
    row.md_number, row.so_number, row.lead_id, row.client_name, row.client_contact,
    row.client_alternate_contact, row.origin_label, row.destination_label,
    row.logistics?.provider_ref, row.logistics?.awb_no, row.logistics?.vehicle_number,
    row.logistics?.driver_name, row.logistics?.driver_number, row.logistics?.assigned_to_name,
    `R-${row.stage_id}`,
  ].some((v) => v != null && String(v).toLowerCase().includes(q));

// What this return brings back — opened by clicking the row. Quantities come off the return stage's
// own items (live: a pickup shortfall already shrank them); "Received" appears once QC is done.
const ReturnItemsModal: React.FC<{ row: ReturnRow | null; onClose: () => void }> = ({ row, onClose }) => {
  const items = row?.items ?? [];
  const qcDone = items.some((i) => i.received_qty != null);
  const total = items.reduce((sum, i) => sum + Number(i.picked_qty || 0), 0);
  const meta = row ? STAGE_META[row.status] : null;
  return (
    <Modal
      open={!!row}
      onCancel={onClose}
      footer={null}
      width={720}
      title={row ? `Return #R-${row.stage_id} · ${row.md_number ?? "—"}` : ""}
    >
      {row && (
        <>
          <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Order">
              <a href={omsOrderPath(row.so_id, { poNumber: row.md_number })} target="_blank" rel="noreferrer">
                {row.so_number}
              </a>
            </Descriptions.Item>
            <Descriptions.Item label="Stage">
              {meta && <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{meta.label}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Customer">{row.client_name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Pickup">
              {row.pickup_date ? dayjs(row.pickup_date).format("DD MMM YYYY") : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="From">{row.origin_label || "—"}</Descriptions.Item>
            <Descriptions.Item label="To">{row.destination_label || "—"}</Descriptions.Item>
            <Descriptions.Item label="Carrier booking" span={2}>
              {row.logistics?.provider_ref ? (
                <Space size={8} wrap>
                  <span style={MUTED}>
                    {CARRIER_META[row.logistics.provider_type]?.label || row.logistics.provider_type}
                  </span>
                  <Typography.Text copyable={{ tooltips: ["Copy booking number", "Copied"] }} strong>
                    {row.logistics.provider_ref}
                  </Typography.Text>
                  {row.logistics.awb_no && row.logistics.awb_no !== row.logistics.provider_ref && (
                    <Typography.Text
                      copyable={{ text: row.logistics.awb_no, tooltips: ["Copy AWB", "Copied"] }}
                      type="secondary"
                    >
                      AWB {row.logistics.awb_no}
                    </Typography.Text>
                  )}
                </Space>
              ) : (
                <Typography.Text type="secondary">Not booked with a carrier yet</Typography.Text>
              )}
            </Descriptions.Item>
          </Descriptions>
          <Table<ReturnStageItem>
            rowKey="allocation_item_id"
            size="small"
            pagination={false}
            dataSource={items}
            locale={{ emptyText: "No items on this return" }}
            columns={[
              {
                title: "Item",
                key: "item",
                render: (_, i) => (
                  <div>
                    <div>{i.product_name}</div>
                    {i.sku && <div style={MUTED}>{i.sku}</div>}
                  </div>
                ),
              },
              {
                title: "Returning",
                dataIndex: "picked_qty",
                align: "right",
                width: 110,
                render: (v) => <strong>{Number(v)}</strong>,
              },
              ...(qcDone
                ? [{
                    title: "Received at QC",
                    key: "received",
                    align: "right" as const,
                    width: 150,
                    render: (_: unknown, i: ReturnStageItem) => {
                      const short = Number(i.picked_qty) - Number(i.received_qty ?? 0);
                      return (
                        <Space size={4}>
                          <span>{Number(i.received_qty ?? 0)}</span>
                          {short > 0 && <Tag color="orange" style={{ marginInlineEnd: 0 }}>{short} short</Tag>}
                          {i.breakage && <Tag color="red" style={{ marginInlineEnd: 0 }}>Broken</Tag>}
                        </Space>
                      );
                    },
                  }]
                : []),
            ]}
            summary={() =>
              items.length > 1 ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right"><strong>{total}</strong></Table.Summary.Cell>
                  {qcDone && <Table.Summary.Cell index={2} />}
                </Table.Summary.Row>
              ) : null
            }
          />
        </>
      )}
    </Modal>
  );
};

const OwnerCell: React.FC<{ row: ReturnRow; teamMembers: TeamMember[]; onSaved: () => void }> = ({
  row, teamMembers, onSaved,
}) => {
  const [saving, setSaving] = useState(false);
  const log = row.logistics;
  if (!log) {
    return (
      <Tooltip title="The owner sits on the carrier booking — this return has none yet.">
        <Typography.Text type="secondary">—</Typography.Text>
      </Tooltip>
    );
  }
  const change = async (val?: string) => {
    setSaving(true);
    const updated = await updateLogisticsOrder(log.id, { assigned_to: val ?? null });
    setSaving(false);
    if (updated) onSaved();
  };
  return (
    <Select
      size="small"
      style={{ width: 130 }}
      placeholder="Unassigned"
      value={log.assigned_to ?? undefined}
      loading={saving}
      allowClear
      showSearch
      options={teamMembers.map((m) => ({ value: m.value, label: m.label }))}
      onChange={(v) => change(v)}
      filterOption={(input, opt) => String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())}
    />
  );
};

const ReturnsPanel: React.FC<Props> = ({ onCountChange }) => {
  const [tab, setTab] = useRememberedTab<StageTab>(TAB_STORAGE_KEY, TAB_KEYS, "all_pending");
  const [openRows, setOpenRows] = useState<ReturnRow[]>([]);
  const [historyRows, setHistoryRows] = useState<ReturnRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [modalStage, setModalStage] = useState<ReturnRow | null>(null);
  const [reassignFor, setReassignFor] = useState<ReturnRow | null>(null);
  const [rescheduleFor, setRescheduleFor] = useState<ReturnRow | null>(null);
  const [reattemptFor, setReattemptFor] = useState<ReturnRow | null>(null);
  const [changeWhFor, setChangeWhFor] = useState<ReturnRow | null>(null);
  const [bookDelhiveryFor, setBookDelhiveryFor] = useState<ReturnRow | null>(null);
  const [vendorReturnFor, setVendorReturnFor] = useState<ReturnRow | null>(null);
  const [issueTarget, setIssueTarget] = useState<IssueTarget | null>(null);
  // The ReturnRow behind the open escalation drawer — its items + stage id feed the
  // shortfall-at-pickup form. Cleared alongside issueTarget.
  const [issueRow, setIssueRow] = useState<ReturnRow | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [itemsFor, setItemsFor] = useState<ReturnRow | null>(null);
  const [notesFor, setNotesFor] = useState<ReturnRow | null>(null);
  const [viewing, setViewing] = useState<LogisticsOrderRow | null>(null);
  const [editing, setEditing] = useState<LogisticsOrderRow | null>(null);
  const [search, setSearch] = useState("");
  const [pickupDay, setPickupDay] = useState<Dayjs | null>(() => dayjs());
  const [owner, setOwner] = useState("");
  const [labourOnly, setLabourOnly] = useState(false);
  const [city, setCity] = useState("");
  const [warehouseId, setWarehouseId] = useState<number | null>(null);
  const [closedSections, setClosedSections] = useState<string[]>([]);
  const [paging, setPaging] = useState<Record<string, { page: number; size: number }>>({});
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const { hasPermission } = usePermissions();
  const canAuthorize = hasPermission("refund.row_action");
  const canUpdate = hasPermission("oms.update");
  const canCancelBeforePickup = hasPermission("return.cancel_before_pickup");
  const canReschedulePickup = hasPermission("return.reschedule_pickup");
  const isAdmin = useIsAdmin() === true;
  const canReturnToVendor = RTV_ENABLED && hasPermission("return.to_vendor");
  const isHistory = HISTORY_TABS.includes(tab);
  // Only the history tabs need their own fetch; the open-worklist tabs share one, so switching
  // between them must not refetch.
  const historyTab = isHistory ? (tab as "authorized" | "vendor_return" | "all") : null;

  const load = useCallback(async () => {
    setLoading(true);
    const [open, history] = await Promise.all([
      listReturns("all_pending"),
      historyTab ? listReturns(historyTab) : Promise.resolve(undefined),
    ]);
    if (open) {
      setOpenRows(open.results ?? []);
      onCountChange?.(open.results?.length ?? 0);
    }
    if (history) setHistoryRows(history.results ?? []);
    setUpdatedAt(Date.now());
    setLoading(false);
  }, [historyTab, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    listTeamMembers().then(setTeamMembers);
  }, []);

  const confirmAction = (row: ReturnRow, action: ReturnRowAction) =>
    Modal.confirm({
      title: `${action.label}?`,
      icon: <ExclamationCircleOutlined />,
      content: `Updates the carrier booking for return #R-${row.stage_id} (MD ${row.md_number ?? "—"}).`,
      okText: action.label,
      cancelText: "Not yet",
      onOk: async () => {
        setBusyKey(`${row.stage_id}:${action.action}`);
        const updated = await runLogisticsAction(action.href, action.label);
        setBusyKey(null);
        if (updated) load();
      },
    });

  // Only before the driver has collected: cancels the carrier booking and releases the SO line's
  // returnable qty server-side. A refund pre-authorized at raise is NOT unwound — that stays a
  // separate Cash Log action.
  const confirmCancelBeforePickup = (row: ReturnRow) =>
    Modal.confirm({
      title: `Cancel return #R-${row.stage_id}?`,
      icon: <ExclamationCircleOutlined />,
      content:
        "Cancels the carrier booking (if any) and releases the returnable qty back on the SO line " +
        "so a fresh return can be raised. If a refund was already pre-authorized at raise, you'll " +
        "need to cancel that separately from the Cash Log — this does NOT unwind the money side.",
      okText: "Cancel return",
      cancelText: "Keep it",
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusyKey(`${row.stage_id}:cancel`);
        const ok = await cancelReturnBeforePickup(row.stage_id);
        setBusyKey(null);
        if (ok) {
          message.success("Return cancelled ✓");
          load();
        }
      },
    });

  const confirmQcDone = (row: ReturnRow) =>
    Modal.confirm({
      title: "Mark QC done?",
      icon: <ExclamationCircleOutlined />,
      content:
        "Confirms the warehouse team has stamped QC on this return. The authorize-refund " +
        "action then becomes available.",
      okText: "Mark QC done",
      cancelText: "Not yet",
      onOk: async () => {
        setBusyKey(`${row.stage_id}:qc`);
        const ok = await markQcComplete(row.stage_id);
        setBusyKey(null);
        if (ok) {
          message.success("QC marked done ✓");
          load();
        }
      },
    });

  const confirmVendorReturnDone = (row: ReturnRow, vr: VendorReturnSummary) =>
    Modal.confirm({
      title: vr.mode === "collect" ? "Vendor collected the goods?" : "Goods delivered to the vendor?",
      icon: <ExclamationCircleOutlined />,
      content: `Closes the vendor return for return #R-${row.stage_id}.`,
      okText: vr.mode === "collect" ? "Mark handed over" : "Mark delivered",
      cancelText: "Not yet",
      onOk: async () => {
        setBusyKey(`${row.stage_id}:vr_done`);
        const ok = await markVendorReturnDone(vr.stage_id);
        setBusyKey(null);
        if (ok) {
          message.success("Vendor return closed ✓");
          load();
        }
      },
    });

  const confirmVendorReturnAction = (row: ReturnRow, action: ReturnRowAction) =>
    Modal.confirm({
      title: `${action.label}?`,
      icon: <ExclamationCircleOutlined />,
      content: `Updates the truck taking return #R-${row.stage_id} back to the vendor.`,
      okText: action.label,
      cancelText: "Not yet",
      onOk: async () => {
        setBusyKey(`${row.stage_id}:vr_${action.action}`);
        const updated = await runLogisticsAction(action.href, action.label);
        setBusyKey(null);
        if (updated) load();
      },
    });

  const confirmCancelVendorReturn = (row: ReturnRow, vr: VendorReturnSummary) =>
    Modal.confirm({
      title: `Cancel the vendor return for #R-${row.stage_id}?`,
      icon: <ExclamationCircleOutlined />,
      content: "Only possible before the vendor credit is in Zoho and before the goods leave.",
      okText: "Cancel vendor return",
      cancelText: "Keep it",
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusyKey(`${row.stage_id}:vr_cancel`);
        const ok = await cancelVendorReturn(vr.stage_id);
        setBusyKey(null);
        if (ok) {
          message.success("Vendor return cancelled ✓");
          load();
        }
      },
    });

  const vendorReturnAction = (row: ReturnRow) => {
    const vr = row.vendor_return;
    if (!vr || !vendorReturnLive(vr) || vr.status !== "credit_filed" || !canReturnToVendor) return null;
    const actions = vr.mode === "ship" ? vr.logistics?.available_actions ?? [] : [];
    if (actions.length) {
      return (
        <Space size={4} wrap>
          {actions.map((a) => (
            <Button key={a.action} size="small" loading={busyKey === `${row.stage_id}:vr_${a.action}`}
                    onClick={() => confirmVendorReturnAction(row, a)}>
              {a.label}
            </Button>
          ))}
        </Space>
      );
    }
    return (
      <Button size="small" type="primary" loading={busyKey === `${row.stage_id}:vr_done`}
              onClick={() => confirmVendorReturnDone(row, vr)}>
        {vr.mode === "collect" ? "Mark handed over" : "Mark delivered"}
      </Button>
    );
  };

  const openDetails = async (row: ReturnRow) => {
    if (!row.logistics) return;
    setBusyKey(`${row.stage_id}:view`);
    const booking = await getLogisticsRow(row.logistics.id);
    setBusyKey(null);
    if (booking) setViewing(booking);
  };

  // The filter bar narrows every tab alike, so the tab counts describe what the desk can see.
  const passesFilters = useCallback(
    (row: ReturnRow) => {
      const q = search.trim().toLowerCase();
      if (q && !matchesSearch(row, q)) return false;
      if (pickupDay && isPrePickup(row)
        && (!row.pickup_date || dayjs(row.pickup_date).isAfter(pickupDay, "day"))) return false;
      if (owner === UNASSIGNED_OWNER && row.logistics?.assigned_to) return false;
      if (owner && owner !== UNASSIGNED_OWNER && row.logistics?.assigned_to !== owner) return false;
      if (labourOnly && !row.is_unloading) return false;
      if (city && row.delivery_city !== city) return false;
      if (warehouseId !== null && row.destination_wh_id !== warehouseId) return false;
      return true;
    },
    [search, pickupDay, owner, labourOnly, city, warehouseId]
  );

  const filteredOpen = useMemo(() => openRows.filter(passesFilters), [openRows, passesFilters]);
  const counts = useMemo(() => {
    const out: Partial<Record<StageTab, number>> = {};
    for (const key of TAB_KEYS) {
      const inTab = IN_TAB[key];
      if (inTab) out[key] = filteredOpen.filter(inTab).length;
    }
    return out;
  }, [filteredOpen]);
  const visibleRows = useMemo(
    () => (isHistory ? historyRows.filter(passesFilters) : filteredOpen.filter(IN_TAB[tab]!)),
    [isHistory, historyRows, filteredOpen, passesFilters, tab]
  );
  const baseCount = isHistory ? historyRows.length : openRows.filter(IN_TAB[tab]!).length;

  const warehouseOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const r of [...openRows, ...historyRows]) {
      if (r.destination_wh_id != null && !seen.has(r.destination_wh_id)) {
        seen.set(r.destination_wh_id, r.destination_label || `Warehouse #${r.destination_wh_id}`);
      }
    }
    return Array.from(seen, ([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [openRows, historyRows]);

  const filtersApplied =
    !!search.trim() || !!pickupDay || !!owner || labourOnly || !!city || warehouseId !== null;
  const clearFilters = () => {
    setSearch("");
    setPickupDay(null);
    setOwner("");
    setLabourOnly(false);
    setCity("");
    setWarehouseId(null);
  };

  // The ONE thing this return needs next, as the row's primary button — everything else is in More.
  const nextAction = (row: ReturnRow) => {
    const log = row.logistics;
    if (row.authorized) {
      return (
        <Space size={4} wrap>
          {!vendorReturnLive(row.vendor_return) && (
            <Typography.Text type="success">Refund authorized</Typography.Text>
          )}
          {vendorReturnAction(row)}
        </Space>
      );
    }
    if (row.qc_completed) {
      return canAuthorize ? (
        <Button type="primary" size="small" onClick={() => setModalStage(row)}>
          Authorize refund
        </Button>
      ) : (
        <Typography.Text type="secondary">Waiting for refund authorization</Typography.Text>
      );
    }
    if (row.received) {
      return canUpdate ? (
        <Button type="primary" size="small" loading={busyKey === `${row.stage_id}:qc`}
                onClick={() => confirmQcDone(row)}>
          Mark QC done
        </Button>
      ) : (
        <Typography.Text type="secondary">Waiting for QC</Typography.Text>
      );
    }
    const actions = canUpdate ? log?.available_actions ?? [] : [];
    // Delhivery reverse-pickup is offered only on the latest return and only while the leg is still
    // switchable (backend sets can_book_delhivery). When it's an OLDER return, a muted hint explains
    // the missing button; when it's the latest but not switchable (already booked / Porter / departed
    // / not intracity), there's nothing to say — no button, no hint.
    const canBookDelhivery = canUpdate && row.can_book_delhivery && !!row.book_delhivery_href;
    const newerReturnHint = !row.can_book_delhivery && row.is_latest_return === false;
    const bookDelhiveryBtn = canBookDelhivery ? (
      <Button
        key="book_delhivery"
        size="small"
        type={actions.length ? "default" : "primary"}
        onClick={() => setBookDelhiveryFor(row)}
      >
        Book Delhivery
      </Button>
    ) : null;

    if (!actions.length && !bookDelhiveryBtn) {
      return (
        <Space size={4} wrap>
          <Typography.Text type="secondary">
            {log ? STAGE_META[row.status]?.next : "No booking yet"}
          </Typography.Text>
          {newerReturnHint && (
            <Typography.Text type="secondary" italic>
              A newer return exists for this order
            </Typography.Text>
          )}
        </Space>
      );
    }
    return (
      <Space size={4} wrap>
        {actions.map((a, i) => (
          <Button key={a.action} size="small" type={i === 0 ? "primary" : "default"}
                  loading={busyKey === `${row.stage_id}:${a.action}`}
                  onClick={() => confirmAction(row, a)}>
            {a.label}
          </Button>
        ))}
        {bookDelhiveryBtn}
        {newerReturnHint && (
          <Typography.Text type="secondary" italic>
            A newer return exists for this order
          </Typography.Text>
        )}
      </Space>
    );
  };

  const moreMenu = (row: ReturnRow): MenuProps["items"] => {
    const log = row.logistics;
    const dead = log?.lifecycle === "cancelled" || log?.lifecycle === "superseded";
    const items: MenuProps["items"] = [
      { key: "view", label: "View all details", disabled: !log, onClick: () => openDetails(row) },
      { key: "order", label: "Open order", onClick: () => window.open(omsOrderPath(row.so_id), "_blank") },
    ];
    if (row.so_id) {
      items.push({ key: "notes", label: "View escalation notes", onClick: () => setNotesFor(row) });
    }
    if (log?.tracking_url) {
      items.push({ key: "track", label: "Open tracking",
                   onClick: () => window.open(log.tracking_url ?? undefined, "_blank") });
    }
    if (canUpdate && log) {
      items.push(
        { type: "divider" },
        {
          key: "escalate",
          label: "Raise escalation",
          onClick: () => {
            setIssueRow(row);
            setIssueTarget({ kind: "stage", queue: "grn", row: _asStageRow(row) });
          },
        },
        { key: "reassign", label: "Move to another carrier", disabled: dead,
          onClick: () => setReassignFor(row) },
      );
    }
    if (canReschedulePickup && isPrePickup(row)) {
      items.push(
        { type: "divider" },
        { key: "reschedule", label: "Reschedule pickup", onClick: () => setRescheduleFor(row) },
        ...(!row.logistics?.vehicle_number && !row.logistics?.driver_name && !row.logistics?.driver_number
          ? [{ key: "change_wh", label: "Change warehouse", onClick: () => setChangeWhFor(row) }]
          : []),
        ...(row.status === "booked"
          ? [{ key: "reattempt", label: "Reattempt pickup", onClick: () => setReattemptFor(row) }]
          : []),
      );
    }
    if (isAdmin && row.status === "picked_up" && !row.received) {
      items.push(
        { type: "divider" },
        { key: "reattempt", label: "Reattempt pickup", onClick: () => setReattemptFor(row) },
      );
    }
    const vr = row.vendor_return;
    if (canReturnToVendor && row.qc_completed && !vendorReturnLive(vr)) {
      items.push(
        { type: "divider" },
        { key: "vr_raise", label: "Return to vendor", onClick: () => setVendorReturnFor(row) },
      );
    }
    if (canReturnToVendor && vr &&["awaiting_stock", "awaiting_bill", "credit_failed"].includes(vr.status)) {
      items.push(
        { type: "divider" },
        { key: "vr_cancel", label: "Cancel vendor return", danger: true,
          onClick: () => confirmCancelVendorReturn(row, vr) },
      );
    }
    if (canCancelBeforePickup && isPrePickup(row)) {
      items.push(
        { type: "divider" },
        { key: "cancel", label: "Cancel return", danger: true,
          onClick: () => confirmCancelBeforePickup(row) },
      );
    }
    return items;
  };

  const columns: ColumnsType<ReturnRow> = [
    {
      title: "PO #",
      key: "po",
      width: 140,
      fixed: "left",
      render: (_, row) => (
        <div>
          {poCell({ po_numbers: row.md_number ? [row.md_number] : [] })}
          <div style={{ fontSize: 11, color: "#8c8c8c", whiteSpace: "nowrap" }}>#R-{row.stage_id}</div>
        </div>
      ),
    },
    {
      title: "Sales order",
      key: "order",
      width: 200,
      fixed: "left",
      render: (_, row) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ whiteSpace: "nowrap" }}>
            <Tooltip title="Click to copy SO number">
              <CopyText value={String(row.so_number)} label="SO number">
                <span style={{ color: "#1677ff" }}>{row.so_number}</span>
              </CopyText>
            </Tooltip>
            <Tooltip title="Open the OMS order details">
              <Button
                type="link"
                size="small"
                icon={<ExportOutlined />}
                href={omsOrderPath(row.so_id, { poNumber: row.md_number })}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ padding: 0, height: "auto", fontSize: 12, marginLeft: 6 }}
              >
                Open
              </Button>
            </Tooltip>
          </div>
          {(row.is_unloading || row.delivery_city === "outstation") && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 3 }}>
              {row.is_unloading && (
                <Tooltip title="Loading needed at the customer's site — set when the return was raised.">
                  <Tag color="volcano" icon={<UploadOutlined />} style={{ marginInlineEnd: 0 }}>
                    loading
                  </Tag>
                </Tooltip>
              )}
              {row.delivery_city === "outstation" && (
                <Tooltip title="Customer is outside every hub's zone">
                  <Tag color="orange" style={{ marginInlineEnd: 0 }}>Outstation client</Tag>
                </Tooltip>
              )}
            </div>
          )}
          {floorLift({
            is_unloading: !!row.is_unloading,
            delivery_floor: row.pickup_floor ?? null,
            delivery_has_lift: row.pickup_has_lift ?? null,
          })}
        </div>
      ),
    },
    {
      title: "Pickup",
      key: "pickup_date",
      width: 100,
      render: (_, row) => (
        <div style={{ fontSize: 12, whiteSpace: "nowrap" }}>
          <div>{row.pickup_date ? dayjs(row.pickup_date).format("DD MMM") : dash(null)}</div>
          {row.created_at && (
            <Tooltip title={`Raised ${dayjs(row.created_at).format("DD MMM YYYY, HH:mm")}`}>
              <div style={{ color: "#888" }}>Raised {dayjs(row.created_at).format("DD MMM")}</div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 190,
      render: (_, row) => {
        const meta = STAGE_META[row.status] ?? { label: row.status, color: "default", next: "" };
        return (
          <Space size={[4, 4]} wrap>
            <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{meta.label}</Tag>
            {row.vendor_return && (
              <Tooltip
                title={
                  row.vendor_return.vendor_credit_error
                    ?? row.vendor_return.hold_reason
                    ?? (row.vendor_return.vendor_credit_number
                      ? `Vendor credit ${row.vendor_return.vendor_credit_number}` : undefined)
                }
              >
                <Tag color={VENDOR_RETURN_META[row.vendor_return.status]?.color}
                     style={{ marginInlineEnd: 0 }}>
                  {VENDOR_RETURN_META[row.vendor_return.status]?.label ?? row.vendor_return.status}
                </Tag>
              </Tooltip>
            )}
            {pickupOverdue(row) && (
              <Tooltip title="Still not collected after the promised pickup day">
                <Tag color="red" style={{ marginInlineEnd: 0 }}>Pickup overdue</Tag>
              </Tooltip>
            )}
            {row.escalation && (
              <Tooltip
                title={
                  <>
                    <div><strong>{row.escalation.issue_type}</strong></div>
                    {row.escalation.remarks && <div>“{row.escalation.remarks}”</div>}
                    {row.escalation.raised_at && (
                      <div style={{ fontSize: 11, opacity: 0.8 }}>
                        raised {dayjs(row.escalation.raised_at).format("DD MMM, HH:mm")}
                      </div>
                    )}
                  </>
                }
              >
                <Tag color="red" icon={<WarningFilled />}
                     style={{ marginInlineEnd: 0, maxWidth: "100%", cursor: "help", ...ELLIPSIS }}>
                  {row.escalation.issue_type || "Escalated"}
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "From",
      key: "from",
      width: 150,
      render: (_, row) => locationCell(row.origin_label, row.origin_lat ?? null, row.origin_long ?? null),
    },
    {
      title: "To",
      key: "to",
      width: 150,
      render: (_, row) =>
        locationCell(row.destination_label, row.destination_lat ?? null, row.destination_long ?? null),
    },
    {
      title: "Customer",
      key: "customer",
      width: 160,
      render: (_, row) =>
        customerCell({
          client_name: row.client_name,
          client_contact: row.client_contact ?? null,
          client_alternate_contact: row.client_alternate_contact ?? null,
        }),
    },
    {
      title: "Carrier",
      key: "carrier",
      width: 110,
      render: (_, row) => {
        const log = row.logistics;
        if (!log) return <span style={{ color: "#bbb" }}>No booking yet</span>;
        const meta = CARRIER_META[log.provider_type] || { label: log.provider_type };
        const tag = <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{meta.label}</Tag>;
        return log.provider_ref ? (
          <Tooltip title="Click to copy carrier ref">
            <CopyText value={log.provider_ref} label="Carrier ref">{tag}</CopyText>
          </Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: "Driver & vehicle",
      key: "driver",
      width: 150,
      render: (_, row) => {
        const log = row.logistics;
        const driver = [log?.driver_name, log?.driver_number].filter(Boolean).join(" · ");
        if (!log?.vehicle_number && !driver) return <span style={{ color: "#bbb" }}>Not assigned</span>;
        return (
          <div style={{ minWidth: 0 }}>
            <div>{dash(log?.vehicle_number)}</div>
            <div style={{ fontSize: 12, color: "#888" }}>{driver || "no driver yet"}</div>
            {log?.trip_name && <div style={{ ...MUTED, ...ELLIPSIS }}>{log.trip_name}</div>}
          </div>
        );
      },
    },
    {
      title: "Owner",
      key: "owner",
      width: 140,
      render: (_, row) =>
        canUpdate ? (
          <div onClick={(e) => e.stopPropagation()}>
            <OwnerCell row={row} teamMembers={teamMembers} onSaved={load} />
          </div>
        ) : (
          dash(row.logistics?.assigned_to_name)
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 240,
      render: (_, row) => (
        <Space size={4} wrap onClick={(e) => e.stopPropagation()}>
          {nextAction(row)}
          <Dropdown trigger={["click"]} menu={{ items: moreMenu(row) }}>
            <Button size="small"
                    loading={busyKey === `${row.stage_id}:view` || busyKey === `${row.stage_id}:cancel`
                             || busyKey === `${row.stage_id}:vr_cancel`}>
              More ▾
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];
  const showOwner = tab !== "authorize_pending" && tab !== "escalated";
  const visibleColumns = columns.filter((c) => showOwner || c.key !== "owner");
  const tableWidth = visibleColumns.reduce((sum, c) => sum + Number(c.width ?? 0), 0);

  const renderTable = (data: ReturnRow[], pagingKey: string = tab) => {
    const ordered = orderByLead(data);
    const spans = ordered.spans;
    const size = paging[pagingKey]?.size ?? 25;
    const pages: ReturnRow[][] = [];
    let current: ReturnRow[] = [];
    for (let i = 0; i < ordered.rows.length; ) {
      const span = spans.get(ordered.rows[i].stage_id)?.size ?? 1;
      if (current.length && current.length + span > size) {
        pages.push(current);
        current = [];
      }
      current.push(...ordered.rows.slice(i, i + span));
      i += span;
    }
    if (current.length) pages.push(current);
    const page = Math.min(paging[pagingKey]?.page ?? 1, Math.max(pages.length, 1));
    const rows = pages[page - 1] ?? [];
    const accent = (row: ReturnRow) => (spans.get(row.stage_id)?.size ?? 1) > 1;
    const tableColumns: ColumnsType<ReturnRow> = visibleColumns.map((c) => {
      if (c.key === "po") {
        return {
          ...c,
          onCell: (row: ReturnRow) => ({
            style: pickupOverdue(row)
              ? { boxShadow: "inset 3px 0 0 #ff4d4f" }
              : accent(row)
                ? { boxShadow: "inset 3px 0 0 #4096ff" }
                : undefined,
          }),
        };
      }
      if (c.key === "customer") {
        return {
          ...c,
          onCell: (row: ReturnRow) => {
            const span = spans.get(row.stage_id);
            if (!span || span.size === 1) return {};
            return span.pos === 0
              ? {
                  rowSpan: span.size,
                  style: { verticalAlign: "top", background: "#fafcff", cursor: "default" },
                  onClick: (e: React.MouseEvent) => e.stopPropagation(),
                }
              : { rowSpan: 0 };
          },
          render: (value: unknown, row: ReturnRow, index: number) => {
            const span = spans.get(row.stage_id);
            const cell = (c.render ? c.render(value as never, row, index) : null) as React.ReactNode;
            if (!span || span.size === 1) return cell;
            return (
              <div>
                {cell}
                <Tag color="blue" bordered={false} style={{ marginTop: 6, marginInlineEnd: 0 }}>
                  {span.size} returns · same enquiry
                </Tag>
              </div>
            );
          },
        };
      }
      return c;
    });
    return (
    <>
    <Table<ReturnRow>
      rowKey="stage_id"
      size="small"
      tableLayout="fixed"
      columns={tableColumns}
      dataSource={rows}
      loading={loading}
      scroll={{ x: tableWidth }}
      onRow={(row) => ({ onClick: () => setItemsFor(row), style: { cursor: "pointer" } })}
      pagination={false}
      locale={{ emptyText: filtersApplied ? "No returns match these filters." : EMPTY_TEXT[tab] }}
    />
    {ordered.rows.length > 25 && (
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <Pagination
          size="small"
          current={page}
          pageSize={size}
          total={pages.length * size}
          showSizeChanger
          pageSizeOptions={[25, 50, 100]}
          showTotal={() => `${ordered.rows.length} returns`}
          onChange={(nextPage, nextSize) =>
            setPaging((prev) => ({
              ...prev,
              [pagingKey]: { page: nextSize !== size ? 1 : nextPage, size: nextSize },
            }))
          }
        />
      </div>
    )}
    </>
    );
  };

  return (
    <>
      <Space wrap size={8} style={{ marginBottom: 12 }}>
        {TAB_KEYS.map((key) => {
          const escalated = key === "escalated" && (counts.escalated ?? 0) > 0;
          const checked = tab === key;
          return (
            <Tag.CheckableTag
              key={key}
              checked={checked}
              onChange={() => setTab(key)}
              style={
                escalated
                  ? {
                      border: "1px solid #ffccc7",
                      padding: "2px 10px",
                      color: checked ? undefined : "#cf1322",
                      background: checked ? "#cf1322" : "#fff1f0",
                    }
                  : { border: "1px solid #eee", padding: "2px 10px" }
              }
            >
              {counts[key] != null ? `${TAB_LABEL[key]} · ${counts[key]}` : TAB_LABEL[key]}
            </Tag.CheckableTag>
          );
        })}
      </Space>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12, marginBottom: 12 }}>
        <div>
          <div style={FILTER_LABEL}>Pickup due by</div>
          <DatePicker
            format="DD MMM"
            placeholder="Any day"
            value={pickupDay}
            onChange={(v) => setPickupDay(v)}
            presets={[
              { label: "Today", value: dayjs() },
              { label: "Tomorrow", value: dayjs().add(1, "day") },
            ]}
          />
        </div>
        <div>
          <div style={FILTER_LABEL}>Delivery city</div>
          <Select
            allowClear
            placeholder="All cities"
            style={{ width: 170 }}
            value={city || undefined}
            onChange={(v) => setCity(v ?? "")}
            onClear={() => setCity("")}
            options={CITY_OPTIONS}
          />
        </div>
        <div>
          <div style={FILTER_LABEL}>Return warehouse</div>
          <Select
            allowClear
            showSearch
            placeholder="All warehouses"
            style={{ width: 200 }}
            value={warehouseId ?? undefined}
            onChange={(v) => setWarehouseId(v ?? null)}
            onClear={() => setWarehouseId(null)}
            options={warehouseOptions}
            optionFilterProp="label"
          />
        </div>
        <div>
          <div style={FILTER_LABEL}>Owner</div>
          <Select
            allowClear
            showSearch
            placeholder="Anyone"
            style={{ width: 180 }}
            value={owner || undefined}
            onChange={(v) => setOwner(v ?? "")}
            onClear={() => setOwner("")}
            options={[
              { value: UNASSIGNED_OWNER, label: "Unassigned (no owner)" },
              ...teamMembers.map((m) => ({ value: m.value, label: m.label })),
            ]}
            filterOption={(input, opt) =>
              String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
        <div>
          <div style={FILTER_LABEL}>Loading needed only</div>
          <div style={{ height: 32, display: "flex", alignItems: "center" }}>
            <Switch checked={labourOnly} onChange={setLabourOnly} />
          </div>
        </div>
        <div>
          <div style={FILTER_LABEL}>Search</div>
          <Input.Search
            allowClear
            placeholder="MD, ENQ, R-id, customer, phone, vehicle, carrier ref…"
            style={{ width: 300 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "flex-end", gap: 8 }}>
          {filtersApplied && <Button onClick={clearFilters}>Clear</Button>}
          <Tooltip title="Refresh now">
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
          </Tooltip>
        </div>
      </div>

      <div style={{ marginBottom: 8, color: "#8c8c8c", fontSize: 13 }}>
        {visibleRows.length === 0
          ? filtersApplied
            ? "No returns match these filters"
            : EMPTY_TEXT[tab]
          : `${visibleRows.length} return${visibleRows.length === 1 ? "" : "s"}`}
        {filtersApplied && visibleRows.length > 0 && (
          <span style={{ marginLeft: 8 }}>· of {baseCount}</span>
        )}
        {updatedAt && <span style={{ marginLeft: 8 }}>· updated {fmtIST(updatedAt, "HH:mm:ss")} IST</span>}
      </div>

      {TAB_SECTIONS[tab] ? (
        <Collapse
          ghost
          activeKey={TAB_SECTIONS[tab]!.map((sec) => sec.key).filter((k) => !closedSections.includes(k))}
          onChange={(keys) => {
            const open = Array.isArray(keys) ? keys : [keys];
            setClosedSections(TAB_SECTIONS[tab]!.map((sec) => sec.key).filter((k) => !open.includes(k)));
          }}
          items={TAB_SECTIONS[tab]!.flatMap((sec) => {
            const sectionRows = visibleRows.filter(sec.match);
            if ((sec.key === "escalation" || sec.key === "outstation") && !sectionRows.length) return [];
            return [{
              key: sec.key,
              label: (
                <Space>
                  <Tooltip title={sec.help}><strong>{sec.label}</strong></Tooltip>
                  <Tag color={sec.key === "escalation" ? "red" : undefined}>{sectionRows.length}</Tag>
                </Space>
              ),
              children: renderTable(sectionRows, `${tab}:${sec.key}`),
            }];
          })}
        />
      ) : (
        renderTable(visibleRows)
      )}

      <ReturnItemsModal row={itemsFor} onClose={() => setItemsFor(null)} />
      {notesFor?.so_id && (
        <CrmIssueDrawer
          salesOrderId={notesFor.so_id}
          open={!!notesFor}
          onClose={() => setNotesFor(null)}
        />
      )}

      {modalStage && (
        <RefundModal
          open={modalStage !== null}
          onClose={() => setModalStage(null)}
          onDone={() => {
            setModalStage(null);
            load();
          }}
          context={RefundModalContext.AUTHORIZE_RETURN}
          stageId={modalStage.stage_id}
          soId={modalStage.so_id}
          userId={modalStage.user_id ?? undefined}
        />
      )}

      <ReassignModal
        open={reassignFor !== null}
        order={
          reassignFor?.logistics
            ? ({ id: reassignFor.logistics.id } as any)   // reassign_modal reads order.id only
            : null
        }
        onClose={() => setReassignFor(null)}
        onReassigned={() => {
          setReassignFor(null);
          load();
        }}
      />

      <ReschedulePickupModal
        row={rescheduleFor}
        onClose={() => setRescheduleFor(null)}
        onRescheduled={() => {
          message.success("Pickup rescheduled ✓");
          load();
        }}
      />

      <VendorReturnModal
        row={vendorReturnFor}
        onClose={() => setVendorReturnFor(null)}
        onRaised={() => {
          message.success("Vendor return raised ✓");
          load();
        }}
      />

      <ReturnDelhiveryModal
        row={bookDelhiveryFor}
        onClose={() => setBookDelhiveryFor(null)}
        onBooked={() => {
          setBookDelhiveryFor(null);
          load();
        }}
      />

      <ChangeWarehouseModal
        row={changeWhFor}
        onClose={() => setChangeWhFor(null)}
        onChanged={() => {
          message.success("Return warehouse changed ✓");
          load();
        }}
      />

      <ReschedulePickupModal
        row={reattemptFor}
        reattempt
        onClose={() => setReattemptFor(null)}
        onRescheduled={() => {
          message.success("Pickup reattempt booked ✓");
          load();
        }}
      />

      <BookingDrawer
        booking={viewing}
        onClose={() => setViewing(null)}
        onEdit={(r) => {
          setViewing(null);
          setEditing(r);
        }}
        onReassign={(r) => {
          setViewing(null);
          const row = [...openRows, ...historyRows].find((x) => x.logistics?.id === r.id);
          if (row) setReassignFor(row);
        }}
      />
      <EditLogisticsModal
        open={!!editing}
        order={editing}
        teamMembers={teamMembers}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />

      <IssueDrawer
        target={issueTarget}
        // Returns-tab escalations only show the return-pickup category — the driver-reported
        // reasons for a pickup not going through.
        categoryFilter={["return_pickup"]}
        // Items + stage id drive the "Shortfall quantity" per-item form inside the drawer.
        items={issueRow?.items}
        returnStageId={issueRow?.stage_id}
        returnReceived={!!issueRow?.received && !issueRow?.qc_completed}
        onClose={() => {
          setIssueTarget(null);
          setIssueRow(null);
        }}
        onRaised={() => {
          setIssueTarget(null);
          setIssueRow(null);
          load();
        }}
      />
    </>
  );
};

// Project a ReturnRow into the PendingStageRow fields IssueDrawer reads. Keyed on the return's own
// stage: returns sharing one truck share a booking, whose lead stage would take every escalation.
function _asStageRow(row: ReturnRow): any {
  return {
    id: row.stage_id,
    po_number: row.md_number ?? row.po_numbers?.[0] ?? null,
    lead_id: row.lead_id ?? null,
    client_name: row.client_name,
    supplier_name: row.client_name,
    warehouse_name: row.destination_label,
  };
}

export default ReturnsPanel;
